import { useEffect, useMemo, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";

import { GET_MEMBER_SHOPPING_CART } from "../graphql/resolvers/member";
import {
  DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART,
  GET_LOCAL_SHOPPING_CART,
  REMOVE_SHOPPING_CART_DISCOUNT,
} from "../graphql/resolvers/cart";
import { useAppContext } from "../components/organisms/Layout/context";
import { GET_AUTH } from "../graphql/resolvers/auth";
import { GET_ACTIVE_ADVENT_CALENDAR } from "../graphql/resolvers/specialPack";
import { ActiveAdventCalendar } from "../types/__generated__/ActiveAdventCalendar";
import { MemberShoppingCart } from "../types/__generated__/MemberShoppingCart";
import { LocalShoppingCart } from "../types/__generated__/LocalShoppingCart";
import { getAuth } from "../types/__generated__/getAuth";
import {
  RemoveShoppingCartDiscount,
  RemoveShoppingCartDiscountVariables,
} from "../types/__generated__/RemoveShoppingCartDiscount";
import {
  DeleteItemsByProductTypeInLocalShoppingCart,
  DeleteItemsByProductTypeInLocalShoppingCartVariables,
} from "../types/__generated__/DeleteItemsByProductTypeInLocalShoppingCart";
import { isLoggedIn } from "./auth";
import { clearGuestBoxIntent, markGuestDirectAddsChosen } from "./guestCheckout";
import { trackingEvents, useTrackEvent } from "./Analytics";
import { FETCH_POLICY_NO_CACHE } from "./constants";
import {
  BuildYourOwnIntent,
  BuildYourOwnProgress,
  clearBuildYourOwnApplied,
  clearBuildYourOwnIntent,
  countCartWineBottles,
  deriveLocalBuildYourOwnProgress,
  getBuildYourOwnOffer,
  getBuildYourOwnProgress,
  getBuildYourOwnSetAside,
  getNonWineCartItemNames,
  getOtherCartLines,
  countCartWineSpend,
  setBuildYourOwnIntent,
} from "./buildYourOwn";
import { applyBuildYourOwnPromoCode, ApplyBuildYourOwnOutcome } from "./buildYourOwnApply";
import { useBuildYourOwnMarkers } from "./useBuildYourOwnMarkers";

export interface BuildYourOwnState {
  /** Null unless a build-your-own calendar is in progress for this visitor. */
  progress: BuildYourOwnProgress | null;
  /** Progress is a local preview (guest cart) rather than the server's. */
  isGuest: boolean;
  /** The member's code was set aside by the backend because the cart holds non-wine items. */
  isSetAside: boolean;
  blockingItemNames: string[];
  /**
   * The backend's reason checkout is blocked right now (an incomplete started
   * calendar), verbatim; null when it can proceed. Guests get no server message,
   * only `progress.blocksCheckout` from the local preview.
   */
  checkoutError: string | null;
}

interface UseBuildYourOwnOptions {
  /** A caller that already holds the member cart query (the cart tray) passes it instead of re-querying. */
  memberCartQuery?: { data: MemberShoppingCart | undefined; loading?: boolean };
}

/**
 * Where this visitor is with a build-your-own advent calendar. Members read the
 * backend's `promoCodeFreebies` on the cart query that every cart mutation already
 * refetches; guests get a preview counted from the local cart until the code can
 * be applied after login.
 */
export const useBuildYourOwn = ({ memberCartQuery }: UseBuildYourOwnOptions = {}): BuildYourOwnState => {
  const loggedIn = isLoggedIn();
  const { intent, applied } = useBuildYourOwnMarkers();
  const ownQuery = useQuery<MemberShoppingCart>(GET_MEMBER_SHOPPING_CART, {
    skip: !loggedIn || !!memberCartQuery,
  });
  const { data: localCartData } = useQuery<LocalShoppingCart>(GET_LOCAL_SHOPPING_CART, {
    skip: loggedIn || !intent,
  });
  /* A guest's stored choice is a snapshot of the promo. The live edition is the
   * source of truth once it has answered — `no-cache`, as the listing hook does,
   * and `errorPolicy: "all"` because the client's default swallows resolver
   * errors into a null edition: changed thresholds or a changed code are
   * adopted (the stored copy follows), and no offer at all — the campaign over
   * or the code withdrawn — retires the choice, so the shop stops promising a
   * box the backend would refuse at login. A failed or pending read keeps the
   * snapshot. */
  const {
    data: adventData,
    loading: isAdventLoading,
    error: adventError,
  } = useQuery<ActiveAdventCalendar>(GET_ACTIVE_ADVENT_CALENDAR, {
    skip: loggedIn || !intent,
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    errorPolicy: "all",
  });
  const liveOffer = useMemo(
    () => getBuildYourOwnOffer(adventData?.getActiveAdventCalendar?.buildYourOwnPromoCode),
    [adventData]
  );
  const hasLiveAnswer = !!intent && adventData !== undefined && !isAdventLoading && !adventError;
  const isOfferWithdrawn = hasLiveAnswer && !liveOffer;
  useEffect(() => {
    if (isOfferWithdrawn) clearBuildYourOwnIntent();
  }, [isOfferWithdrawn]);
  const guestIntent = intent && !isOfferWithdrawn ? liveOffer ?? intent : null;
  const isStoredIntentStale = !!guestIntent && JSON.stringify(guestIntent) !== JSON.stringify(intent);
  useEffect(() => {
    if (isStoredIntentStale && guestIntent) setBuildYourOwnIntent(guestIntent);
  }, [isStoredIntentStale, guestIntent]);
  const memberCartData = memberCartQuery ? memberCartQuery.data : ownQuery.data;
  const loading = memberCartQuery ? !!memberCartQuery.loading : ownQuery.loading;

  const cart = loggedIn ? memberCartData?.me?.shoppingCart : null;
  const items = cart?.shoppingcartitemSet;

  /* The applied marker outlives the code when an order is placed or the code
   * expires. Once the cart holds no code and nothing that could have set it
   * aside, the backend would have restored it if it still existed. */
  const isMarkerStale =
    loggedIn &&
    !!applied &&
    !loading &&
    !!cart &&
    !cart.discountCode &&
    getNonWineCartItemNames(items).length === 0;
  useEffect(() => {
    if (isMarkerStale) clearBuildYourOwnApplied();
  }, [isMarkerStale]);

  if (loggedIn) {
    const { isSetAside, blockingItemNames } = getBuildYourOwnSetAside({
      appliedPromoCode: applied?.promoCode ?? null,
      discountCode: cart?.discountCode,
      grants: cart?.promoCodeFreebies,
      items,
    });
    return {
      progress: getBuildYourOwnProgress(cart?.promoCodeFreebies),
      isGuest: false,
      isSetAside,
      blockingItemNames,
      checkoutError: cart?.promoCodeFreebiesCheckoutError ?? null,
    };
  }

  const localItems = localCartData?.localShoppingCart?.items;
  const progress = guestIntent
    ? deriveLocalBuildYourOwnProgress(
        guestIntent,
        countCartWineBottles(localItems),
        countCartWineSpend(localItems)
      )
    : null;
  return { progress, isGuest: true, isSetAside: false, blockingItemNames: [], checkoutError: null };
};

/** Drops the calendar: removes the member's code or forgets a guest's choice. */
export const useRemoveBuildYourOwn = () => {
  const trackEvent = useTrackEvent();
  const { data: authData } = useQuery<getAuth>(GET_AUTH);
  const [removeDiscount, { loading }] = useMutation<
    RemoveShoppingCartDiscount,
    RemoveShoppingCartDiscountVariables
  >(REMOVE_SHOPPING_CART_DISCOUNT, {
    refetchQueries: [{ query: GET_MEMBER_SHOPPING_CART }],
    awaitRefetchQueries: true,
  });

  const remove = async () => {
    if (isLoggedIn()) {
      const memberId = authData?.auth?.memberId;
      if (!memberId) return;
      await removeDiscount({ variables: { input: { memberId } } });
      clearBuildYourOwnApplied();
    } else {
      clearBuildYourOwnIntent();
    }
    trackEvent(trackingEvents.TRACKING_EVENT_REMOVED_BUILD_YOUR_OWN_ADVENT, { is_logged_in: isLoggedIn() });
  };

  return { remove, isRemoving: loading };
};

export type StartBuildYourOwnOutcome = ApplyBuildYourOwnOutcome;

export interface StartBuildYourOwnOptions {
  /** Drop the cart lines a previous attempt named in `blockedBy`. */
  removeOtherItems?: boolean;
}

/**
 * Starts a build-your-own calendar for this visitor. A member gets the code on
 * their cart now (it works on an empty cart; a "monthly box" cart is switched
 * to one-off first — see applyBuildYourOwnPromoCode). A guest's choice is kept
 * locally and the code applied right after login/signup + cart transfer
 * (helpers/auth.js), so their wines head for the one-off cart, not a first
 * box. Either way the order is wines only: anything else already in the cart
 * is named in `blockedBy`, and nothing changes, until the caller passes
 * `removeOtherItems` on the shopper's say-so.
 */
export const useStartBuildYourOwn = () => {
  const client = useApolloClient();
  const loggedIn = isLoggedIn();
  const { data: authData } = useQuery<getAuth>(GET_AUTH);
  const { data: localCartData } = useQuery<LocalShoppingCart>(GET_LOCAL_SHOPPING_CART, { skip: loggedIn });
  const { minBottlesForFreeShipping, standardShippingFee } = useAppContext();
  const [deleteLocalItemsByProductType] = useMutation<
    DeleteItemsByProductTypeInLocalShoppingCart,
    DeleteItemsByProductTypeInLocalShoppingCartVariables
  >(DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART);
  const [isStarting, setIsStarting] = useState(false);

  const startAsGuest = async (
    offer: BuildYourOwnIntent,
    removeOtherItems: boolean
  ): Promise<StartBuildYourOwnOutcome> => {
    const otherLines = getOtherCartLines(localCartData?.localShoppingCart?.items);
    if (otherLines.length > 0) {
      if (!removeOtherItems) {
        return { ok: false, blockedBy: otherLines.map((item) => item.product.name ?? "").filter(Boolean) };
      }
      const productTypeIds = Array.from(
        new Set(
          otherLines.flatMap((item) =>
            item.product.productType?.id != null ? [item.product.productType.id] : []
          )
        )
      );
      await productTypeIds.reduce<Promise<unknown>>(
        (previous, productTypeId) =>
          previous.then(() =>
            deleteLocalItemsByProductType({
              variables: {
                input: {
                  productTypeId,
                  minBottlesForFreeShipping: minBottlesForFreeShipping ?? 0,
                  standardShippingFee: standardShippingFee ?? 0,
                },
              },
            })
          ),
        Promise.resolve()
      );
    }
    setBuildYourOwnIntent(offer);
    clearGuestBoxIntent();
    markGuestDirectAddsChosen();
    return { ok: true };
  };

  const start = async (
    offer: BuildYourOwnIntent,
    { removeOtherItems = false }: StartBuildYourOwnOptions = {}
  ): Promise<StartBuildYourOwnOutcome> => {
    setIsStarting(true);
    try {
      if (!loggedIn) return await startAsGuest(offer, removeOtherItems);
      const memberId = authData?.auth?.memberId;
      if (!memberId) return { ok: false, message: "Please log in and try again." };
      return await applyBuildYourOwnPromoCode(client, memberId, offer.promoCode, {
        removeOtherItems,
        perNumberOfWines: offer.perNumberOfWines,
      });
    } finally {
      setIsStarting(false);
    }
  };

  return { start, isStarting };
};
